/**
 * =============================================================================
 *  CustomerCaseModal.jsx
 * =============================================================================
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactModal from "react-modal";
import axios from "axios";
import { format, isSameDay, addDays } from "date-fns";

/* ---------------------------------------------------------------------------
 *  Configure <ReactModal> — accessibility root
 * ------------------------------------------------------------------------ */
ReactModal.setAppElement("#root");

/* =============================================================================
 *  1. VISUAL STYLING CONSTANTS
 * ============================================================================= */
const modalStyles = {
  content: {
    maxWidth: "1800px",
    width: "96%",
    margin: "auto",
    padding: "36px",
    borderRadius: "16px",
    border: "4px solid #111827", // Tailwind gray-900
    boxShadow: "0 20px 40px rgba(0,0,0,.4)",
    overflow: "auto",
    backgroundColor: "#F9FAFB", // Tailwind gray-50
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
};

/**
 * BookingStatusBadge
 * Renders a styled pill/badge for the booking status.
 */
const BookingStatusBadge = ({ status, loading }) => {
  // Show pulsing badge while loading
  if (loading) {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-700 animate-pulse">
        Loading…
      </span>
    );
  }

  // Map statuses to color styles
  const styles = {
    "ready for customer":
      "bg-yellow-100 text-yellow-800 border border-yellow-300",
    "returned to customer":
      "bg-green-100 text-green-800 border border-green-300",
  };

  // Normalize key and choose style (fallback = gray neutral)
  const key = (status || "").toLowerCase();
  const cls = styles[key] || "bg-gray-100 text-gray-800 border border-gray-300";

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${cls}`}
    >
      {status || "Unknown"}
    </span>
  );
};

/* =============================================================================
 *  2. HELPER UTILITIES
 * ============================================================================= */
const currency = (n) =>
  n?.toLocaleString("nb-NO", { style: "currency", currency: "NOK" });

/* Format labour hours with exactly 2 decimals */
const hoursFmt = (h) =>
  h == null
    ? "—"
    : Number(h).toLocaleString("nb-NO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

/* ---------------------------------------------------------------------------
 *  Build HTML table for missing / parts lines (open lines only)
 * ------------------------------------------------------------------------ */
const tableList = (lines) => {
  if (!lines.length) return "";

  /* CSS som følger tabellen */
  const styleBlock =
    "<style>" +
    ".bill-table{width:100%;border-collapse:collapse;margin:16px 0;" +
    'font:15px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}' +
    ".bill-table th{padding:8px;border:1px solid #e5e5e5;background:#374151;color:#fff;font-weight:600;}" +
    ".bill-table th.qty{text-align:center;}" +
    ".bill-table th.num{text-align:right;}" +
    ".bill-table td{padding:6px 8px;border:1px solid #e5e5e5;}" +
    ".bill-table tbody tr:nth-child(even){background:#f9fafb;}" +
    ".bill-table td.qty{text-align:center;white-space:nowrap;}" +
    ".bill-table td.num{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums;}" +
    "</style>";

  /* Tabell-hode */
  const head =
    '<table class="bill-table"><thead><tr>' +
    "<th>Beskrivelse</th>" +
    '<th class="qty">Antall</th>' +
    '<th class="num">Pris</th>' +
    '<th class="num">Sum</th>' +
    "</tr></thead><tbody>";

  /* Rader */
  const rows = lines
    .map((l) => {
      const qty =
        l.lineType === "labour"
          ? hoursFmt(l.hours ?? l.quantity) + " t"
          : String(l.quantity);

      return (
        "<tr>" +
        `<td>${l.productDescription || l.productName}</td>` +
        `<td class="qty">${qty}</td>` +
        `<td class="num">${currency(l.salesPriceIncVat)}</td>` +
        `<td class="num">${currency(l.lineTotal)}</td>` +
        "</tr>"
      );
    })
    .join("");

  const foot = "</tbody></table>";

  /* Ferdig HTML-streng */
  return styleBlock + head + rows + foot;
};

/* ---------------------------------------------------------------------------
 *  Convert plain‑text template → HTML (inject tables & totals for **open** lines)
 * ------------------------------------------------------------------------ */

const plainToHtml = (text, openMissing, openPartsLab) => {
  const parseMarkdown = (str) =>
    str.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // ---------- totals – re-computed every call ----------
  const totals = {
    missingTotal: currency(
      openMissing.reduce((s, l) => s + (l.lineTotal || 0), 0)
    ),
    partsTotal: currency(
      openPartsLab.reduce((s, l) => s + (l.lineTotal || 0), 0)
    ),
    grandTotal: currency(
      [...openMissing, ...openPartsLab].reduce(
        (s, l) => s + (l.lineTotal || 0),
        0
      )
    ),
  };

  // ---------- convert paragraphs ----------
  const htmlBody = text
    .split(/\n{2,}/)
    .map(
      (p) =>
        `<p style="margin-bottom:1em;">${parseMarkdown(p.trim()).replace(
          /\n/g,
          "<br/>"
        )}</p>`
    )
    .join("\n");

  return (
    "<div style=\"font:15px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI'," +
    'Roboto,Helvetica,Arial,sans-serif;color:#111827;">' +
    htmlBody
      .replace("[TABLE_MISSING]", tableList(openMissing))
      .replace("[TABLE_PARTS]", tableList(openPartsLab))
      .replace("[MISSING_TOTAL]", totals.missingTotal)
      .replace("[PARTS_TOTAL]", totals.partsTotal)
      .replace("[GRAND_TOTAL]", totals.grandTotal) +
    "</div>"
  );
};

/* ---------------------------------------------------------------------------
 *  3. Templates
 * ------------------------------------------------------------------------ */
const tplMissing = (d) => {
  return `
Hei ${d.firstName},

Takk for at du leide elsykkel hos oss – vi håper du har hatt en god opplevelse med Joule!
Ved gjennomgang av sykkelen etter retur, oppdaget vi at følgende tilbehør manglet:

[TABLE_MISSING]

**Total verdi:** [MISSING_TOTAL]

Vi ber deg om å returnere tilbehøret enten ved å levere det hos oss i åpningstidene (mandag–fredag kl. 10–17), eller sende det til:

Joule AS
Gjerdrums vei 11
0484 Oslo

Dersom vi ikke mottar tilbehøret innen ${d.deadline}, vil vi dessverre måtte belaste betalingskortet ditt for det oppgitte beløpet. Vi håper å unngå dette og setter pris på en rask tilbakemelding.
Har du spørsmål eller trenger mer informasjon, er det bare å svare på denne e-posten – vi hjelper deg gjerne!

Takk for at du valgte Joule, og ha en fortsatt fin dag!`;
};

const tplParts = (d) => {
  return `
Hei ${d.firstName},

Takk for at du leide elsykkel hos oss – vi håper du har hatt en god opplevelse med Joule!

Ved inspeksjon av sykkelen etter retur har vi registrert slitasje, skader eller mangler som går utover det som regnes som normal bruk. Dette kan for eksempel være tydelige slag- eller støtskader, feil bruk, manglende vedlikehold – eller at deler mangler. 
Slike forhold påvirker sykkelens funksjon og sikkerhet, og må utbedres før den kan benyttes av neste kunde.

[TABLE_PARTS]

**Totalt beløp:** [PARTS_TOTAL]

Kostnadene dekker reservedeler og arbeid knyttet til istandsetting av sykkelen. Beløpet vil bli belastet ditt registrerte betalingskort om 5 virkedager.
Dersom du har spørsmål eller ønsker å diskutere vurderingen før beløpet trekkes, er du velkommen til å svare på denne e-posten – vi tar gjerne en prat.
 
Takk for at du valgte Joule – vi håper å se deg igjen snart.

Ha en fortsatt fin dag!`;
};

const tplBoth = (d) => {
  return `
Hei ${d.firstName},

Takk for at du leide elsykkel hos oss – vi håper du har hatt en god opplevelse med Joule!

Etter en grundig gjennomgang av sykkelen ved retur, har vi registrert to forhold vi ønsker å informere deg om:

1️⃣ **Manglende utstyr**
[TABLE_MISSING]
**Verdi:** [MISSING_TOTAL]

Dersom du fremdeles har utstyret, kan du returnere det innen ${d.deadline}, enten ved å levere det til oss i åpningstidene (mandag–fredag kl. 10–17) eller sende det til:

Joule AS
Gjerdrums vei 11
0484 Oslo

Leveres utstyret innen fristen, frafaller dette beløpet. Etter fristen vil vi belaste betalingskortet ditt.

2️⃣ **Mangler eller slitasje utover normal bruk**
[TABLE_PARTS]
**Verdi:** [PARTS_TOTAL]

Kostnadene dekker arbeid og nødvendige reservedeler for å gjøre sykkelen klar for neste kunde.

**Totalt beløp:** [GRAND_TOTAL]

Dersom vi ikke hører fra deg eller mottar det manglende utstyret innen ${d.deadline}, vil det totale beløpet bli belastet ditt registrerte betalingskort kort tid etter fristens utløp.

Ta gjerne kontakt ved spørsmål eller om du mener noe er feil – vi hjelper deg gjerne!
Takk for at du valgte Joule, og ha en fortsatt fin dag!`;
};

const tplService = (d) => {
  return `
Hei ${d.firstName},

I henhold til punkt 6 i leievilkårene dekker abonnementet service og utskiftning av deler som slites ved normal bruk. 
Reparasjoner eller utskiftninger som skyldes forhold utenfor normal slitasje – som punktering eller manglende vedlikehold – belastes leietaker.
 
Ved denne servicen ble det utført arbeid og byttet deler som faller utenfor normal bruksslitasje:

[TABLE_PARTS]

**Totalt beløp:** [PARTS_TOTAL]

Beløpet vil bli belastet ditt registrerte betalingskort om 5 virkedager.
Dersom du har spørsmål, eller ønsker å diskutere vurderingen før beløpet trekkes, er du velkommen til å svare på denne e-posten – vi hjelper deg gjerne.
 
Takk for at du valgte Joule, og ha en fortsatt fin dag!`;
};

/* ---------------------------------------------------------------------------
 *  Velg riktig mal og bygg første utkast
 * ------------------------------------------------------------------------ */

const buildFirstDraft = (missing, partsLab, customer, source) => {
  const openMissing = missing.filter((l) => l.status === "open");
  const openPartsLab = partsLab.filter((l) => l.status === "open");

  const data = {
    firstName: customer.fullName.split(" ")[0],
    // 🟢 open-items only
    missingTable: tableList(openMissing),
    partsTable: tableList(openPartsLab),
    openMissing,
    openPartsLab,
    deadline: format(addDays(new Date(), 14), "dd.MM.yyyy"),
  };

  if (source === "Service") return tplService(data);
  if (openMissing.length && openPartsLab.length) return tplBoth(data);
  if (openMissing.length) return tplMissing(data);
  if (openPartsLab.length) return tplParts(data);
  return "";
};

// ---------------------------------------------------------------
// Build the Zendesk ticket subject (includes serial number)
// ---------------------------------------------------------------
const buildSubject = (missing, partsLab, serial, source) => {
  if (source === "Service")
    return `Fakturering etter service hos Joule – SN ${serial}`;
  if (missing.length && partsLab.length)
    return `Manglende utstyr og skadevurdering etter retur av sykkel – SN ${serial}`;
  if (missing.length)
    return `Manglende utstyr etter endt abonnement hos Joule – SN ${serial}`;
  if (partsLab.length)
    return `Vurdering etter innlevering av sykkel hos Joule – SN ${serial}`;
  return `Customer case – SN ${serial}`;
};

/* ---------------------------------------------------------------------------
 *  EditableNote — inline “click-to-edit” textarea
 * ------------------------------------------------------------------------ */
const EditableNote = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  const commit = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };

  /* ---------------- RENDER ---------------- */
  return editing ? (
    <textarea
      className="border rounded w-full p-1 text-sm focus:outline-joule focus:ring-1 focus:ring-joule"
      rows={2}
      value={draft}
      autoFocus
      onBlur={commit}
      onChange={(e) => setDraft(e.target.value)}
    />
  ) : (
    <div
      className="text-sm cursor-pointer hover:bg-gray-100 rounded p-1"
      title="Click to edit note"
      onClick={() => setEditing(true)}
    >
      {value || <span className="italic text-gray-500">Add note…</span>}
    </div>
  );
};

/* ---------------------------------------------------------------------------
 *  LineRow — single table row (aligned & zebra-striped by the table)
 * ------------------------------------------------------------------------ */
const LineRow = ({ line, section, onStatus, onNote, onAction, busyId }) => {
  const isBusy = busyId === line._id;

  const buttons =
    section === "missing"
      ? [
          {
            label: "Mark as returned",
            cb: () => {
              const confirmed = window.confirm(
                `Are you sure you want to mark "${line.productName}" as returned? This will affect the price.`
              );
              if (confirmed) onAction(line, "returned");
            },
            hide: line.status === "closed",
          },
        ]
      : [
          {
            label: "Delete",
            cb: () => onAction(line, "delete"),
            hide: line.status !== "open",
          },
        ];

  /* ---------------- RENDER ---------------- */
  return (
    <tr className="align-middle">
      {/* --- Common columns --- */}
      <td className="px-3 py-2 font-medium">{line.lineType}</td>

      {/* “parts”-only columns */}
      {section === "parts" && (
        <td className="px-3 py-2">{line.productNo || "—"}</td>
      )}

      <td className="px-3 py-2">{line.productName}</td>

      <td className="px-3 py-2 text-xs text-gray-600">
        {line.productDescription}
      </td>

      <td className="px-3 py-2 text-center">{line.quantity}</td>

      {section === "parts" && (
        <td className="px-3 py-2 text-center">{hoursFmt(line.hours)}</td>
      )}

      <td className="px-3 py-2 text-right">
        {currency(line.salesPriceIncVat)}
      </td>
      <td className="px-3 py-2 text-right">{currency(line.lineTotal)}</td>

      {/* Invoice number (new column) */}
      <td className="px-3 py-2 text-center text-xs text-gray-500">
        {line.invoiceNumber ?? "—"}
      </td>

      {/* Status dropdown */}
      <td className="px-3 py-2">
        <select
          value={line.status}
          disabled={isBusy}
          onChange={(e) => onStatus(line._id, e.target.value)}
          className="border rounded p-1 text-sm w-36"
        >
          {["open", "awaiting customer", "waived", "billed", "closed"].map(
            (s) => (
              <option key={s}>{s}</option>
            )
          )}
        </select>
      </td>

      {/* Note */}
      <td className="px-3 py-2 w-56">
        <EditableNote value={line.note} onSave={(n) => onNote(line, n)} />
      </td>

      {/* Row actions */}
      <td className="px-3 py-2 whitespace-nowrap">
        {buttons
          .filter((b) => !b.hide)
          .map((b) => (
            <button
              key={b.label}
              disabled={isBusy}
              onClick={b.cb}
              className="bg-joule text-white text-xs py-1 px-3 rounded hover:bg-gray-900 disabled:opacity-50"
            >
              {b.label}
            </button>
          ))}
      </td>
    </tr>
  );
};

/* =============================================================================
 *  3. MAIN COMPONENT — CustomerCaseModal
 * ============================================================================= */
const CustomerCaseModal = ({ isOpen, caseData, closeModal, onCaseUpdated }) => {
  /* ------------------------------ STATE ---------------------------------- */
  const [workingCase, setWorkingCase] = useState(caseData);
  const [busy, setBusy] = useState(null); // lineId | "missingCharge" | "partsCharge"

  // Local state for booking status (only relevant if case.source === "Service")
  const [bookingStatus, setBookingStatus] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  // --- Zendesk messaging ---
  const [ticketId, setTicketId] = useState(workingCase.zendeskTicketId || null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [messagesLoaded, setMessagesLoaded] = useState(false);

  // remember whether we have already pre-filled the textarea (true = done)
  const hasInitialDraft = useRef(false);
  const [showPreview, setShowPreview] = useState(false);

  // reset the text area when modal closes → next open can pre-fill again
  useEffect(() => {
    if (!isOpen) hasInitialDraft.current = false;
    if (!isOpen) setMessagesLoaded(false);
  }, [isOpen]);

  /* Sync props → state */
  useEffect(() => setWorkingCase(caseData), [caseData]);

  /* Keep local ticketId in sync with the case */
  useEffect(() => {
    setTicketId(workingCase?.zendeskTicketId || null);
  }, [workingCase?.zendeskTicketId]);

  // ────────────────────────────────────────────────────────────────
  // LOAD ZENDESK COMMENTS  ➜  sets messages[]  +  messagesLoaded flag
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    /* ❶ No ticket yet ⇒ we instantly know there is no history */
    if (!ticketId) {
      setMessages([]);
      setMessagesLoaded(true);
      return;
    }

    /* ❷ We *do* have a ticket ⇒ start loading */
    setMessagesLoaded(false);

    (async () => {
      try {
        const { data } = await axios.get(
          `/api/zendesk/tickets/${ticketId}/comments`
        );
        setMessages(data);
      } finally {
        setMessagesLoaded(true); // mark done even on error
      }
    })();
  }, [isOpen, ticketId]);

  // ────────────────────────────────────────────────────────────────
  // Fetch booking status of the customer case if source= "Service"
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Only fetch when modal is open, case source is "Service" and bookingId exists
    if (!isOpen) return;
    if (workingCase?.source !== "Service") return;
    if (!workingCase?.bookingId) return;

    setBookingLoading(true);

    axios
      .get(`/api/bookings/${workingCase.bookingId}`)
      .then((res) => {
        // bookingStatus should be either "ready for customer" or "returned to customer"
        setBookingStatus(res.data?.bookingStatus || null);
      })
      .catch((err) => {
        console.error("[CustomerCaseModal] booking fetch failed:", err);
        setBookingStatus(null);
      })
      .finally(() => setBookingLoading(false));
  }, [isOpen, workingCase?.bookingId, workingCase?.source]);

  /* ----------------------------- AJAX HELPERS ---------------------------- */
  const putCase = async (payload) => {
    const { data } = await axios.put(
      `/api/customer-cases/${workingCase._id}`,
      payload
    );
    setWorkingCase(data.customerCase);
    onCaseUpdated(data.customerCase);
  };

  /* --------------------------- BILLING HELPERS --------------------------- */
  const getOpenLines = (lines) => lines.filter((l) => l.status === "open");

  const chargeLines = async (lines, busyKey, messageLabel) => {
    if (!lines.length) return;
    setBusy(busyKey);

    try {
      const payload = {
        // The grand total – we trust lineTotal
        amount: lines.reduce((sum, l) => sum + (l.lineTotal || 0), 0),
        order_id: workingCase.orderId,
        message: `Fakturering i forbindelse med returinpeksjon av sykkel – ${workingCase.serialNumber}`,
        products: lines.map((l) => ({
          product: l.productName,
          amount: l.salesPriceIncVat, // NOK per unit / per hour
          tax_percent: 25,
          //  ── KEY CHANGE ────────────────────────
          quantity:
            l.lineType === "labour"
              ? l.hours || 1 // use hours for labour
              : l.quantity, // pieces for everything else
        })),
      };

      const { data } = await axios.post(
        "/api/circuly/one-time-payment",
        payload
      );
      const invoiceNumber = data.data?.invoice_number ?? data.invoice_number;

      await Promise.all(
        lines.map((l) =>
          putCase({
            action: "markBilled",
            lineId: l._id,
            invoice_number: invoiceNumber,
          })
        )
      );
    } finally {
      setBusy(null);
    }
  };

  /* ------------------------ ROW-LEVEL HELPERS --------------------------- */
  const lineAction = async (line, action) => {
    setBusy(line._id);
    try {
      await putCase({ action, lineId: line._id });
    } finally {
      setBusy(null);
    }
  };

  const updateLineStatus = (id, status) =>
    putCase({ action: "updateLineStatus", lineId: id, status });

  const updateLineNote = (line, note) =>
    putCase({ action: "updateLineNote", lineId: line._id, note });

  const saveCaseNote = (note) => putCase({ action: "updateCaseNote", note });

  // --- CASE STATUS CHANGE (solve Zendesk ticket if case is Closed) ----------
  const handleCaseStatusChange = async (nextStatus) => {
    await putCase({ action: "updateCaseStatus", status: nextStatus });

    // If we have a Zendesk ticket on this case, solve it when case closes
    if (nextStatus === "Closed" && ticketId) {
      try {
        await axios.put(`/api/zendesk/tickets/${ticketId}/solve`, {
          html_body: "<p>Case closed in Joule — solving ticket.</p>",
          public: false, // internal note
        });
      } catch (err) {
        console.error(
          "Zendesk solve ticket failed:",
          err?.response?.data || err
        );
        alert("Case closed, but solving the Zendesk ticket failed. See logs.");
      }
    }
  };

  /* ================================================================ */
  /*            ADD-PART  (client only – server does the lookup)      */
  /* ================================================================ */
  const [showAddPart, setShowAddPart] = useState(false); // toggle mini-form
  const [newPartNo, setNewPartNo] = useState(""); // product number typed
  const [newQty, setNewQty] = useState(1); // quantity typed
  const [addingPart, setAddingPart] = useState(false); // spinner

  const addPartLine = async () => {
    if (!newPartNo.trim() || newQty <= 0) return;
    setAddingPart(true);
    try {
      await putCase({
        action: "addPartLine",
        productNo: newPartNo.trim(),
        quantity: Number(newQty),
      });
      /* reset UI */
      setShowAddPart(false);
      setNewPartNo("");
      setNewQty(1);
    } finally {
      setAddingPart(false);
    }
  };

  /* ---------- ADD-LABOUR ---------- */
  const [showAddLabour, setShowAddLabour] = useState(false);
  const [newMinutes, setNewMinutes] = useState("");
  const [addingLabour, setAddingLabour] = useState(false);

  const addLabourLine = async () => {
    const mins = Number(newMinutes);
    if (!mins || mins <= 0) return;
    setAddingLabour(true);
    try {
      await putCase({
        action: "addLabourLine",
        minutes: mins, // server converts → hours
      });
      setShowAddLabour(false);
      setNewMinutes("");
    } finally {
      setAddingLabour(false);
    }
  };

  /* --- MESSAGE HELPERS ----------------------------------------------------- */

  /** Return sender meta (name, role) for a Zendesk comment               */
  const getSender = (msg, customer, agentName = "Joule Support") => {
    // treat any comment whose author-id matches the customer OR whose
    // email-from matches the customer as a customer message
    const isCustomer =
      msg.author_id === customer.zendeskUserId ||
      msg.via?.source?.from?.address === customer.email;

    return {
      name: isCustomer ? customer.fullName : agentName,
      role: isCustomer ? "customer" : "agent", // also covers internal notes
    };
  };

  /** Avatar – initials in a circle (no external images needed)            */
  const Avatar = ({ name }) => (
    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-gray-700 shrink-0">
      {name
        .split(" ")
        .slice(0, 2)
        .map((n) => n[0])
        .join("")}
    </div>
  );

  // ----------------------- ZENDESK MESSAGES -----------------------
  const sendMessage = async () => {
    if (!draft.trim()) return;
    setSending(true);

    try {
      // Filter only open lines
      const openMissing = missing.filter((l) => l.status === "open");
      const openPartsLab = partsLab.filter((l) => l.status === "open");

      // Build subject and html once
      const subject = buildSubject(
        openMissing,
        openPartsLab,
        workingCase.serialNumber,
        workingCase.source
      );
      const html = plainToHtml(draft.trim(), openMissing, openPartsLab);

      let zendeskId = ticketId;

      if (zendeskId) {
        // Ticket exists → just add comment
        await axios.put(`/api/zendesk/tickets/${zendeskId}`, {
          html_body: html,
        });
      } else {
        /* ── STEP 1: create empty ticket ───────────────────────────── */
        const missingTags = openMissing.length ? ["Mangler_utstyr"] : [];
        const partsTags =
          openPartsLab.length && workingCase.source === "Service"
            ? ["Skade_service"]
            : openPartsLab.length
            ? ["Skade_retur"]
            : [];
        const allTags = Array.from(new Set([...missingTags, ...partsTags]));

        const { data: ticket } = await axios.post("/api/zendesk/tickets", {
          customer: {
            fullName: workingCase.customer.fullName,
            email: workingCase.customer.email,
          },
          serialNumber: workingCase.serialNumber,
          subject,
          orderId: workingCase.orderId,
          tags: allTags,
          type:
            workingCase.source === "Return Inspection"
              ? "avslutning"
              : workingCase.source === "Service"
              ? "hendelse"
              : undefined,
        });

        zendeskId = ticket.id;
        setTicketId(zendeskId);
        await putCase({ action: "saveZendeskTicket", ticketId: zendeskId });

        /* ── STEP 2: immediately add first public comment ──────────── */
        await axios.put(`/api/zendesk/tickets/${zendeskId}`, {
          html_body: html,
        });
      }

      // Record outbound on the case (sets firstOutboundAt / lastOutboundAt)
      const { data: msgRes } = await axios.post(
        `/api/customer-cases/${workingCase._id}/message`,
        { subject, body: html }
      );
      if (msgRes?.customerCase) {
        setWorkingCase(msgRes.customerCase);
        onCaseUpdated?.(msgRes.customerCase);
      }

      setDraft("");

      // Refresh thread
      const { data: cmts } = await axios.get(
        `/api/zendesk/tickets/${zendeskId}/comments`
      );
      setMessages(cmts);
    } finally {
      setSending(false);
      setShowPreview(false);
    }
  };

  /* --------------------------- DERIVED DATA ----------------------------- */
  const missing = useMemo(
    () => workingCase.caseLines.filter((l) => l.lineType === "missingItem"),
    [workingCase.caseLines]
  );

  const parts = useMemo(
    () => workingCase.caseLines.filter((l) => l.lineType === "part"),
    [workingCase.caseLines]
  );

  const labour = useMemo(
    () => workingCase.caseLines.filter((l) => l.lineType === "labour"),
    [workingCase.caseLines]
  );

  const partsLab = useMemo(() => [...parts, ...labour], [parts, labour]);

  const openMissingLines = useMemo(
    () => getOpenLines(missing),
    [missing] // ← trygg; referansen endres bare når missing endres
  );

  const openPartsLabLines = useMemo(() => getOpenLines(partsLab), [partsLab]);

  const missingOpenTotal = useMemo(
    () => openMissingLines.reduce((s, l) => s + (l.lineTotal || 0), 0),
    [openMissingLines]
  );

  const partsLabOpenTotal = useMemo(
    () => openPartsLabLines.reduce((s, l) => s + (l.lineTotal || 0), 0),
    [openPartsLabLines]
  );

  // ---------------------------------------------------------------
  // Pre-fill the composer ONCE per modal-open **iff**
  //  • there is no Zendesk thread yet, and
  //  • the agent has not started typing
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return; // modal closed
    if (!messagesLoaded) return; // still fetching comments
    if (hasInitialDraft.current) return; // already did it
    if (messages.length > 0) return; // history exists

    const firstDraft = buildFirstDraft(
      missing,
      partsLab,
      workingCase.customer,
      workingCase.source
    );

    if (firstDraft) {
      setDraft(firstDraft);
      setShowPreview(true);
      hasInitialDraft.current = true; // lock-in
    }
  }, [
    isOpen,
    ticketId,
    messages.length, // zero → no thread
    missing,
    messagesLoaded,
    partsLab,
    workingCase.customer,
    workingCase.source,
  ]);

  /* ------------------------------ RENDER ------------------------------- */
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      contentLabel="Customer Case"
      style={modalStyles}
    >
      {/* HEADER */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight text-gray-900">
            Customer&nbsp;Case&nbsp;—&nbsp;
            <span className="text-joule">{workingCase.serialNumber}</span>
          </h2>
          <p className="text-sm text-gray-500">
            Created{" "}
            {workingCase.createdAt
              ? format(new Date(workingCase.createdAt), "dd MMM yyyy HH:mm")
              : "—"}
          </p>
        </div>

        <button
          onClick={closeModal}
          className="bg-red-600 text-white rounded-md px-5 py-2 text-sm font-medium shadow hover:bg-red-700"
        >
          Close
        </button>
      </div>
      {/* CASE META DATA */}
      <div className="bg-white rounded-lg shadow p-6 mb-10 grid md:grid-cols-2 gap-x-8 gap-y-4">
        <div>
          <span className="font-semibold text-gray-600">Subscription ID:</span>{" "}
          {workingCase.subscriptionId}
        </div>
        <div>
          <span className="font-semibold text-gray-600">Order ID:</span>{" "}
          {workingCase.orderId || "—"}
        </div>
        <div>
          <span className="font-semibold text-gray-600">Customer:</span>{" "}
          {workingCase.customer.fullName}
        </div>
        <div>
          <span className="font-semibold text-gray-600">Phone:</span>{" "}
          {workingCase.customer.phone}
        </div>
        <div>
          <span className="font-semibold text-gray-600">Email:</span>{" "}
          {workingCase.customer.email}
        </div>
        <div>
          <span className="font-semibold text-gray-600">Status:</span>{" "}
          <select
            value={workingCase.caseStatus}
            onChange={(e) => handleCaseStatusChange(e.target.value)}
            className="border rounded p-1 text-sm w-40"
          >
            {["Open", "Awaiting Customer", "Partially Resolved", "Closed"].map(
              (s) => (
                <option key={s}>{s}</option>
              )
            )}
          </select>
        </div>
        {workingCase.source === "Service" && (
          <>
            <div>
              <span className="font-semibold text-gray-600">Booking ID:</span>{" "}
              {workingCase.bookingId || "—"}
            </div>
            <div>
              <span className="font-semibold text-gray-600">
                Booking Status:
              </span>{" "}
              <BookingStatusBadge
                status={bookingStatus}
                loading={bookingLoading}
              />
            </div>
          </>
        )}
        <div className="md:col-span-2">
          <span className="font-semibold text-gray-600">Case note:</span>
          <EditableNote value={workingCase.note} onSave={saveCaseNote} />
        </div>
      </div>
      {/* ====================================================================
       *  SECTION 1 — MISSING ITEMS
       * ================================================================= */}
      {missing.length > 0 && (
        <>
          <h3 className="text-xl font-semibold mb-6 text-joule">
            Missing Items
          </h3>

          <div className="rounded-lg shadow overflow-hidden mb-4">
            {/* Scroll wrapper keeps the header sticky */}
            <div className="overflow-auto max-h-72">
              <table className="min-w-full text-sm divide-y divide-gray-200">
                <thead className="bg-gray-200 sticky top-0 z-10">
                  <tr className="align-middle">
                    {[
                      "Type",
                      "Item",
                      "Description",
                      "Qty",
                      "Unit Price",
                      "Total",
                      "Invoice #",
                      "Status",
                      "Note",
                      "",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-left font-semibold text-gray-900"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {missing.map((ln, idx) => (
                    <LineRow
                      key={ln._id}
                      line={ln}
                      section="missing"
                      onStatus={updateLineStatus}
                      onNote={updateLineNote}
                      onAction={lineAction}
                      busyId={busy}
                      /* zebra striping via table group—even rows get bg */
                      className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sum + Charge card */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-gray-200 rounded-lg p-4 my-8 shadow-inner">
            <div className="text-lg font-semibold">
              Sum of open missing items:{" "}
              <span className="text-joule">{currency(missingOpenTotal)}</span>
            </div>
            <button
              onClick={() =>
                chargeLines(openMissingLines, "missingCharge", "missing items")
              }
              disabled={!openMissingLines.length || busy === "missingCharge"}
              className="mt-3 sm:mt-0 bg-joule text-white text-sm font-medium py-2 px-6 rounded-md shadow hover:bg-gray-900 disabled:opacity-60"
            >
              {busy === "missingCharge" ? (
                <>
                  <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Charging…
                </>
              ) : (
                "Charge all open"
              )}
            </button>
          </div>
        </>
      )}
      {/* ====================================================================
       *  SECTION 2 — PARTS & LABOUR
       * ================================================================= */}
      {partsLab.length > 0 && (
        <>
          <h3 className="text-xl font-semibold mb-6 text-joule">
            Parts &amp; Labour
          </h3>

          <div className="rounded-lg shadow overflow-hidden mb-4">
            <div className="overflow-auto max-h-80">
              <table className="min-w-full text-sm divide-y divide-gray-200">
                <thead className="bg-gray-200 sticky top-0 z-10">
                  <tr className="align-middle">
                    {[
                      "Type",
                      "Product No",
                      "Name",
                      "Description",
                      "Qty",
                      "Hours",
                      "Unit Price",
                      "Total",
                      "Invoice #",
                      "Status",
                      "Note",
                      "",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-left font-semibold text-gray-900"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {partsLab.map((ln, idx) => (
                    <LineRow
                      key={ln._id}
                      line={ln}
                      section="parts"
                      onStatus={updateLineStatus}
                      onNote={updateLineNote}
                      onAction={lineAction}
                      busyId={busy}
                      className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ─── “Add” controls: part & labour share the same row ─── */}
          <div className="flex flex-wrap gap-6 mb-6 items-end">
            {/* ADD PART --------------------------------------------------------- */}
            {showAddPart ? (
              <div className="flex gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600">
                    Product&nbsp;No
                  </label>
                  <input
                    className="border rounded p-1 text-sm w-40 focus:outline-joule focus:ring-1 focus:ring-joule"
                    value={newPartNo}
                    onChange={(e) => setNewPartNo(e.target.value)}
                    placeholder="RB-12345"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">
                    Qty
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="border rounded p-1 text-sm w-20 text-center focus:outline-joule focus:ring-1 focus:ring-joule"
                    value={newQty}
                    onChange={(e) => setNewQty(e.target.value)}
                  />
                </div>
                <button
                  onClick={addPartLine}
                  disabled={addingPart}
                  className="bg-joule text-white text-xs font-semibold py-2 px-4 rounded hover:bg-gray-900 disabled:opacity-60"
                >
                  {addingPart ? "Adding…" : "Add part"}
                </button>
                <button
                  onClick={() => setShowAddPart(false)}
                  className="bg-red-600 text-white text-xs font-semibold py-2 px-4 rounded hover:bg-red-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddPart(true)}
                className="bg-gray-200 text-gray-800 text-xs font-medium py-1.5 px-3 rounded hover:bg-gray-300"
              >
                + Add part
              </button>
            )}

            {/* ADD LABOUR -------------------------------------------------------- */}
            {showAddLabour ? (
              <div className="flex gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600">
                    Minutes&nbsp;worked
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="border rounded p-1 text-sm w-24 text-center focus:outline-joule focus:ring-1 focus:ring-joule"
                    value={newMinutes}
                    onChange={(e) => setNewMinutes(e.target.value)}
                    autoFocus
                  />
                </div>
                <button
                  onClick={addLabourLine}
                  disabled={addingLabour}
                  className="bg-joule text-white text-xs font-semibold py-2 px-4 rounded hover:bg-gray-900 disabled:opacity-60"
                >
                  {addingLabour ? "Adding…" : "Add labour"}
                </button>
                <button
                  onClick={() => setShowAddLabour(false)}
                  className="bg-red-600 text-white text-xs font-semibold py-2 px-4 rounded hover:bg-red-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddLabour(true)}
                className="bg-gray-200 text-gray-800 text-xs font-medium py-1.5 px-3 rounded hover:bg-gray-300"
              >
                + Add labour
              </button>
            )}
          </div>

          {/* Sum + Charge card */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-gray-200 rounded-lg p-4 my-8 shadow-inner">
            <div className="text-lg font-semibold">
              Sum of open parts &amp; labour:{" "}
              <span className="text-joule">{currency(partsLabOpenTotal)}</span>
            </div>
            <button
              onClick={() =>
                chargeLines(openPartsLabLines, "partsCharge", "parts & labour")
              }
              disabled={!openPartsLabLines.length || busy === "partsCharge"}
              className="mt-3 sm:mt-0 bg-joule text-white text-sm font-medium py-2 px-6 rounded-md shadow hover:bg-gray-900 disabled:opacity-60"
            >
              {busy === "partsCharge" ? (
                <>
                  <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Charging…
                </>
              ) : (
                "Charge all open"
              )}
            </button>
          </div>
        </>
      )}

      {/* ====================================================================
       *  SECTION — CUSTOMER MESSAGES
       * ================================================================= */}
      <h3 className="text-xl font-semibold mt-12 mb-6 text-joule">
        Customer&nbsp;Messages
      </h3>
      <div className="bg-white rounded-lg shadow p-4 mb-14">
        {/* Conversation ------------------------------------------------------- */}
        <section className="space-y-4">
          {messages.map((msg, idx) => {
            const sender = getSender(msg, workingCase.customer);
            const isAgent = sender.role === "agent";
            const prevMsg = messages[idx - 1];
            const sameAuthor = prevMsg && prevMsg.author_id === msg.author_id;
            const newDate =
              idx === 0 ||
              !isSameDay(
                new Date(msg.created_at),
                new Date(prevMsg.created_at)
              );

            return (
              <React.Fragment key={msg.id}>
                {/* ---- Date divider (Today / Yesterday / 18 May 2025 …) --- */}
                {newDate && (
                  <div className="text-center text-[11px] text-gray-400 select-none">
                    {format(new Date(msg.created_at), "eeee d MMM yyyy")}
                  </div>
                )}

                <div
                  className={`flex ${
                    isAgent ? "justify-end" : "justify-start"
                  }`}
                >
                  {/* Avatar on the "outside" of the bubble (only on first in a stack) */}
                  {!isAgent && !sameAuthor && <Avatar name={sender.name} />}

                  <div
                    className={[
                      "relative max-w-[75%] rounded-2xl p-3 shadow text-sm whitespace-pre-wrap",
                      isAgent
                        ? "bg-joule text-white rounded-br-none"
                        : "bg-gray-100 text-gray-900 rounded-bl-none",
                    ].join(" ")}
                  >
                    {/* name + internal badge (only on first in a stack) */}
                    {!sameAuthor && (
                      <div className="absolute -top-5 left-0 text-xs font-semibold flex items-center space-x-2">
                        <span>{sender.name}</span>
                        {!msg.public && (
                          <span className="bg-amber-300 text-gray-900 rounded px-1.5 py-0.5 text-[10px] tracking-wide">
                            INTERNAL
                          </span>
                        )}
                      </div>
                    )}

                    {msg.body}

                    <div className="mt-2 text-[11px] opacity-70 text-right">
                      {format(new Date(msg.created_at), "HH:mm")}
                    </div>
                  </div>

                  {/* Avatar for agent on the right (first in a stack only) */}
                  {isAgent && !sameAuthor && <Avatar name={sender.name} />}
                </div>
              </React.Fragment>
            );
          })}

          {messages.length === 0 && (
            <p className="italic text-gray-500">No conversation yet.</p>
          )}
        </section>

        {/* Composer */}
        <div className="mt-4">
          {showPreview ? (
            <div className="flex gap-4">
              {/* Editable draft */}
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={10}
                className="w-full border rounded-md p-2 focus:outline-joule focus:ring-1 focus:ring-joule resize-none"
              />

              {/* HTML Preview */}
              <div
                className="w-full border rounded-md p-3 overflow-auto bg-gray-50 prose"
                style={{ maxHeight: 300 }}
                dangerouslySetInnerHTML={{
                  __html: plainToHtml(
                    draft,
                    openMissingLines,
                    openPartsLabLines
                  ),
                }}
              />

              {/* Send button */}
              <div className="flex-shrink-0">
                <button
                  onClick={sendMessage}
                  disabled={sending}
                  className="h-full bg-joule text-white px-6 py-2 rounded-md hover:bg-gray-900 disabled:opacity-60"
                >
                  {ticketId ? "Send" : "Send & Create Ticket"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-4">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={6}
                className="w-full border rounded-md p-2 focus:outline-joule focus:ring-1 focus:ring-joule resize-none"
                placeholder="Write a message to the customer…"
              />

              <div className="flex-shrink-0">
                <button
                  onClick={sendMessage}
                  disabled={sending}
                  className="h-full bg-joule text-white px-6 py-2 rounded-md hover:bg-gray-900 disabled:opacity-60"
                >
                  {ticketId ? "Send" : "Send & Create Ticket"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ReactModal>
  );
};

export default CustomerCaseModal;
